import React, { useState } from 'react'
import { Box, Button, Card, CardContent, FormControl, FormControlLabel, IconButton, Radio, RadioGroup, Stack, SvgIcon, Typography, useMediaQuery } from '@mui/material';
import { setApplication } from 'api/application';
import { useSnackbar } from 'components/snackbar';
import { toTitleCase } from 'utils/Utils';
import { useTheme } from '@mui/material/styles';


/**
 * ExternalJobApplicationDialog is a React component that is used to display a dialog for external job applications.
 * It takes the following props:
 * - onClose: A function that is called when the component should close.
 * - jobId: The id of the job.
 * - jobDetails: The details of the job.
 * - tj_score: The score of the job.
 * - handleRefetchJob: A function to refetch the job data.
 * - downloadResume: A function to download the resume.
 * - externalJobStatus: A function to update the external job status.
 * The component displays a card with the job's title, description, and buttons to apply, save, or hide the job.
 * If the user clicks on the apply button, the component will call the handleJobInprogress function.
 * If the user clicks on the save button, the component will call the handleSavedJobs function.
 * If the user clicks on the hide button, the component will call the handleHideJob function.
 * If the user clicks on the close button, the component will call the onClose function.
 * The component also displays a loading indicator when the user is submitting the application.
 */
const ExternalJobApplicationDialog = ({ onClose, jobId, jobDetails, tj_score, handleRefetchJob, downloadResume, externalJobStatus }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [selectedOption, setSelectedOption] = useState('radio-1');
    const [isUpdating, setIsUpdating] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    /**
     * Handles the change of the radio button option.
     * @param {object} event The event object.
     */
    const handleRadioChange = (event) => {
        setSelectedOption(event.target.value);
    };
    /**
     * Handles the buttons to be rendered based on the selected option.
     * @param {string} value The selected option.
     * @returns {JSX.Element} The buttons to be rendered.
     */
    const handleButtons = (value) => {
        if (value === 'radio-1') {
            return (<>
                <Button fullWidth className="cancel-btn" variant="outlined" size="medium" onClick={onClose}>Cancel</Button>
                <Button fullWidth className="continue-btn" variant="contained" size="medium" disabled={isUpdating} onClick={handleAppliedJob}>
                    {isUpdating ? "Applied..." : "Apply"}
                </Button>
            </>)
        }
        else if (value === 'radio-2') {
            return (<>
                <Button fullWidth className="cancel-btn" variant="outlined" size="medium" onClick={downloadResume}>Select & Download Resume</Button>
                <Button fullWidth className="continue-btn" variant="contained" size="medium" disabled={isUpdating} onClick={handleContinueWithApplication}>
                    {isUpdating ? 'In-progress...' : 'Continue with application'}
                </Button>
            </>)
        } else if (value === 'radio-3') {
            return (<>
                <Button fullWidth className="cancel-btn" variant="outlined" size="medium" onClick={onClose}>Cancel</Button>
                <Button fullWidth className="continue-btn" variant="contained" size="medium" disabled={isUpdating} onClick={handleSavedJobs}>
                    {isUpdating ? "Moving..." : "Move to Saved Jobs"}
                </Button>
            </>)
        } else {
            return (<>
                <Button fullWidth className="cancel-btn" variant="outlined" size="medium" onClick={onClose}>Cancel</Button>
                <Button fullWidth className="continue-btn" variant="contained" size="medium" disabled={isUpdating} onClick={handleHideJob}>
                    {isUpdating ? "Removing..." : "Remove Application"}
                </Button>
            </>)
        }
    };
    /**
     * Handles the saving of the job for later.
     * When the user clicks on the save button, this function is called.
     * It updates the job status to 'saved' and calls the handleRefetchJob function to refetch the job data.
     * It also calls the onClose function to close the dialog.
     */
    const handleSavedJobs = async () => {
        try {
            setIsUpdating(true);
            const response = await setApplication({ jobId, jobDetails, status: 'saved', tj_score });
            if (response && response?.success) {
                enqueueSnackbar('Job saved successfully for later', { variant: 'success' });
                handleRefetchJob();
                onClose();
            } else {
                enqueueSnackbar('Error in updating job status', { variant: 'error' });
            }
        } catch (error) {
            console.error('Error in updating job status', error);
        } finally {
            setIsUpdating(false);
        }
    };
    /**
     * Handles the hiding of the job.
     * When the user clicks on the hide button, this function is called.
     * It updates the job status to 'hidden' and calls the handleRefetchJob function to refetch the job data.
     * It also calls the onClose function to close the dialog.
     */
    const handleHideJob = async () => {
        try {
            setIsUpdating(true);
            const [deleteApplicationResponse] = await Promise.all([setApplication({ jobId, type: 'deleteApplication' })]);
            if (deleteApplicationResponse && deleteApplicationResponse?.data && deleteApplicationResponse?.success) {
                enqueueSnackbar('Job removed successfully', { variant: 'success' });
                handleRefetchJob();
                onClose();
            } else {
                enqueueSnackbar('Failed to remove job. Please try again.', { variant: 'error' });
            }
        } catch (error) {
            console.error('Error in hiding job', error);
        } finally {
            setIsUpdating(false);
        }
    };
    /**
     * Handles the application of the job.
     * When the user clicks on the apply button, this function is called.
     * It updates the job status to 'applied' and calls the handleRefetchJob function to refetch the job data.
     * It also calls the onClose function to close the dialog.
     */
    const handleAppliedJob = async () => {
        try {
            setIsUpdating(true);
            const response = await setApplication({ jobId, status: 'applied' });
            if (response && response?.success) {
                enqueueSnackbar('Job applied successfully', { variant: 'success' });
                if (jobDetails) {
                    jobDetails.status = 'applied';
                    jobDetails.applied_at = new Date().toISOString();
                }
                handleRefetchJob();
                onClose();
            } else {
                enqueueSnackbar('Failed to apply for the job. Please try again.', { variant: 'error' });
            }
        } catch (error) {
            console.error('Error in applying for the job:', error);
            enqueueSnackbar('An unexpected error occurred. Please try again.', { variant: 'error' });
        } finally {
            setIsUpdating(false);
        }
    };
    /**
     * Handles the continue with application button click.
     * Opens the external link associated with the job and marks the job status as 'application pending'.
     */
    const handleContinueWithApplication = () => {
        const externalLink = jobDetails?.external_link;
        window.open(externalLink);
        handleJobInprogress();
    };
    /**
     * Handles the application of the job.
     * When the user clicks on the continue with application button, this function is called.
     * It updates the job status to 'application pending' and calls the externalJobStatus function to open the external link.
     * It also calls the onClose function to close the dialog.
     */
    const handleJobInprogress = async () => {
        try {
            setIsUpdating(true);
            const response = await setApplication({ jobId, jobDetails, status: 'application pending', tj_score });
            if (response?.success && response?.data != null) {
                const company = response?.data?.job_id?.company;
                const title = response?.data?.job_id?.title;
                enqueueSnackbar(`Your application to ${toTitleCase(company)} for the ${title} role is in progress`, { variant: 'info' });
                jobDetails.status = response?.data?.status;
                jobDetails.applied_at = response?.data?.applied_at;
                // externalJobStatus();
                onClose('open External Job Dialog');
            } else {
                enqueueSnackbar(response?.message, { variant: 'error' });
            }
        } catch (error) {
            console.error('Error submitting application:', error);
        } finally {
            setIsUpdating(false);
        }
    };

    return (
        <Card variant="outlined">
            <CardContent>
                <Box mb={3}>
                    <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                        <Typography className="title">What action do you wish to take for this job?</Typography>
                        {!isMobile && <IconButton className="close-dialog-btn" aria-label="delete" color="primary" onClick={onClose}>
                            <SvgIcon sx={{ width: '20px', height: '20px' }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <g clip-path="url(#clip0_456_20993)">
                                        <path d="M7 7L17 17M17 7L7 17L17 7Z" stroke="#111827" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_456_20993">
                                            <rect width="24" height="24" fill="white" />
                                        </clipPath>
                                    </defs>
                                </svg>
                            </SvgIcon>
                        </IconButton>}
                    </Stack>
                </Box>

                <Box sx={{ padding: "0 10px" }}>
                    <FormControl fullWidth>
                        <RadioGroup
                            aria-labelledby="demo-radio-buttons-group-label"
                            value={selectedOption}
                            onChange={handleRadioChange}
                            name="radio-buttons-group"
                        >
                            <FormControlLabel
                                value="radio-1"
                                control={<Radio />}
                                className={selectedOption === 'radio-1' && "active-radio"}
                                label={<Box>
                                    <Typography className="option-title">Already applied</Typography>
                                    <Typography className="option-text">You may view, track and update the status of your application in Applied Jobs folder</Typography>
                                </Box>}
                                sx={{ marginBottom: "1rem" }}
                            />
                            <FormControlLabel
                                value="radio-2"
                                control={<Radio />}
                                className={selectedOption === 'radio-2' && "active-radio"}
                                label={<Box>
                                    <Typography className="option-title">Apply for job</Typography>
                                    <Typography className="option-text">You will be redirected to an external website to complete the application process</Typography>
                                </Box>}
                                sx={{ marginBottom: "1rem" }}
                            />
                            <FormControlLabel
                                value="radio-4"
                                control={<Radio />}
                                className={selectedOption === 'radio-4' && "active-radio"}
                                label={<Box>
                                    <Typography className="option-title">Delete application</Typography>
                                    <Typography className="option-text">This application will no longer be available in your Saved Jobs folder</Typography>
                                </Box>}
                            />
                        </RadioGroup>
                    </FormControl>
                </Box>

                <Box mt={3}>
                    <Stack direction="row" justifyContent={'center'} alignItems={'center'} spacing={2}>
                        {handleButtons(selectedOption)}
                    </Stack>
                </Box>
            </CardContent>
        </Card>
    )
}

export default ExternalJobApplicationDialog